'use client'

// React Imports
import { useState, useCallback, useRef, useEffect } from 'react'

// MUI Imports
import { useParams } from 'next/navigation'

import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Backdrop from '@mui/material/Backdrop'
import IconButton from '@mui/material/IconButton'

import { useTheme } from '@mui/material/styles'

// Component Imports
import IconifyIcon from '@/@core/components/icon'
import CompactView from './CompactView'

// Hook Imports
import { useWeatherTranslations } from '@/hooks/useTranslations'

// Next.js Imports

// Utils Imports
import {
  filterFutureHourly,
  getDayOfWeek,
  getDayPeriodInfo,
  formatTimeString,
  formatTemperature,
  formatPercentage,
  formatWindSpeed,
  formatPressure,
  formatVisibility,
  getUVIndexColor,
  formatUVIndex,
  getAirQualityColorByIndex,
  formatAirQuality
} from '@/utils/weatherUtils'

// Types
import type { WeatherData } from '@/types/weather/weatherTypes'

interface ExpandedOverlayProps {
  data: WeatherData
  isExpanded: boolean
  onClose: () => void
  glassmorphismStyle: Record<string, any>
  renderWeatherIcon: (conditionCode: number, size?: number, isDay?: boolean, timeString?: string) => React.ReactNode
  originalPosition: { top: number; left: number; right: number; width: number; height: number } | null
}

const ExpandedOverlay = ({
  data,
  isExpanded,
  onClose,
  glassmorphismStyle,
  renderWeatherIcon,
  originalPosition
}: ExpandedOverlayProps) => {
  const theme = useTheme()
  const { t: _t } = useWeatherTranslations()
  const params = useParams()
  const currentLang = (params?.lang as string) || 'pt'
  const chartRef = useRef<HTMLDivElement>(null)

  // Estados para interatividade
  const [selectedHourIndex, setSelectedHourIndex] = useState(0)
  const [hoveredHourIndex, setHoveredHourIndex] = useState<number | null>(null)
  const [isInitialized, setIsInitialized] = useState(false)
  const [currentDayIndex, setCurrentDayIndex] = useState(0)

  // Criar carrossel contínuo de todos os horários disponíveis
  const getAllHours = useCallback(() => {
    const allHours = []

    // Começar com horas futuras de hoje
    const todayFutureHours = filterFutureHourly(data.forecast[0]?.hourlyData || [])

    allHours.push(...todayFutureHours)

    // Adicionar todas as horas dos próximos dias

    for (let i = 1; i < data.forecast.length; i++) {
      const dayHours = data.forecast[i]?.hourlyData || []

      allHours.push(...dayHours)
    }


    return allHours
  }, [data.forecast])

  const hourlyDataToShow = getAllHours()

  // Obter hora selecionada (hover tem prioridade sobre click)
  const activeHourIndex = hoveredHourIndex !== null ? hoveredHourIndex : selectedHourIndex
  const activeHourData = hourlyDataToShow[activeHourIndex]

  // Função para obter o nome do dia baseado na hora ativa (apenas para exibição)
  const getActiveDayName = () => {
    if (!activeHourData) return _t('days.today')

    const hourDate = new Date(activeHourData.time)
    const today = new Date()

    // Normalizar as datas para comparação (apenas ano, mês, dia)
    const hourDateOnly = new Date(hourDate.getFullYear(), hourDate.getMonth(), hourDate.getDate())
    const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate())

    if (hourDateOnly.getTime() === todayOnly.getTime()) {
      return _t('days.today')
    }

    // Usar diretamente a data da hora para obter o dia da semana
    const dayName = hourDate.toLocaleDateString(currentLang === 'en' ? 'en-US' : 'pt-BR', {
      weekday: 'long'
    })

    return dayName
  }

  // Scroll do gráfico
      const scrollChart = useCallback((direction: 'left' | 'right') => {
    if (!chartRef.current) return

    const scrollAmount = 80 // largura de cada hora

    chartRef.current.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth'
    })
  }, [])

  // Navegação entre dias
  const navigateDay = useCallback((direction: 'prev' | 'next') => {
    const totalDays = data.forecast.length

    if (direction === 'prev' && currentDayIndex > 0) {
      setCurrentDayIndex(currentDayIndex - 1)
    } else if (direction === 'next' && currentDayIndex < totalDays - 1) {
      setCurrentDayIndex(currentDayIndex + 1)
    }
  }, [currentDayIndex, data.forecast.length])



          // Inicializar quando o componente abrir
  useEffect(() => {
    if (isExpanded && !isInitialized) {
      // Sempre começar com o primeiro horário
      setSelectedHourIndex(0)
      setHoveredHourIndex(null)
      setIsInitialized(true)
    }
  }, [isExpanded, isInitialized])

  // Reset quando fechar o componente
  useEffect(() => {
    if (!isExpanded) {
      setIsInitialized(false)
      setSelectedHourIndex(0)
      setHoveredHourIndex(null)
    }
  }, [isExpanded])

  // Renderizar vista expandida
  const renderExpandedContent = () => (
    <Box sx={{ mt: 2 }}>
            {/* Previsão dos Próximos Dias */}
      <Box sx={{ mb: { xs: 2, md: 3 } }}>
        <Typography variant='body1' fontWeight={600} sx={{
          fontSize: { xs: '0.8rem', md: '0.9rem' },
          mb: { xs: 1.5, md: 2 },
          textAlign: 'center'
        }}>
          {_t('forecast.title')}
        </Typography>

        {/* Layout Desktop: Todos os dias lado a lado */}
        <Box sx={{ display: { xs: 'none', md: 'block' } }}>
          <Stack direction='row' spacing={2} sx={{ justifyContent: 'space-around' }}>
            {data.forecast.slice(0, 3).map((day, index) => {
              const dayName = index === 0 ? _t('days.today') :
                             getDayOfWeek(day.date, currentLang === 'en' ? 'en' : 'pt')

              return (
                <Stack key={day.date} spacing={1} sx={{ flex: 1, alignItems: 'center' }}>
                  {/* Nome do dia */}
                  <Typography variant='caption' fontWeight={600} sx={{
                    fontSize: '0.9rem',
                    color: 'text.primary',
                    textAlign: 'center'
                  }}>
                    {dayName}
                  </Typography>

                  {/* Layout reorganizado: Ícone e temperaturas centralizados, informações ao lado */}
                  <Stack direction='row' alignItems='flex-start' spacing={2}>
                    {/* Ícone e temperaturas centralizados */}
                    <Stack alignItems='center' spacing={0.5}>
                      {/* Ícone do clima - sempre claro e maior */}
                      <Box>
                        {renderWeatherIcon(day.dayData.condition.code, 40, true)}
                      </Box>

                      {/* Temperaturas embaixo do ícone */}
                      <Stack direction='row' alignItems='center' spacing={0.4}>
                        <IconifyIcon icon='ri:arrow-up-line' fontSize='0.8rem' color={theme.palette.error.main} />
                        <Typography variant='caption' fontWeight={600} sx={{
                          fontSize: '0.85rem',
                          color: 'text.primary'
                        }}>
                          {Math.round(day.dayData.temperature.max)}°
                        </Typography>
                        <IconifyIcon icon='ri:arrow-down-line' fontSize='0.8rem' color={theme.palette.info.main} />
                        <Typography variant='caption' sx={{
                          fontSize: '0.8rem',
                          color: 'text.secondary'
                        }}>
                          {Math.round(day.dayData.temperature.min)}°
                        </Typography>
                      </Stack>
                    </Stack>

                    {/* Informações adicionais em duas colunas */}
                    <Stack direction='row' spacing={1.5}>
                      {/* Coluna 1: Sunrise, Sunset, Humidity */}
                      <Stack spacing={0.3}>
                        {/* Nascer do sol */}
                        <Stack direction='row' alignItems='center' spacing={0.4}>
                          <IconifyIcon icon='ri:sun-line' fontSize='0.8rem' color={theme.palette.warning.main} />
                          <Typography variant='caption' sx={{
                            fontSize: '0.75rem',
                            color: 'text.secondary'
                          }}>
                            {_t('current.sunrise')}: {day.dayData.astro.sunrise.split(' ')[0]}
                          </Typography>
                        </Stack>

                        {/* Pôr do sol */}
                        <Stack direction='row' alignItems='center' spacing={0.4}>
                          <IconifyIcon icon='ri:moon-line' fontSize='0.8rem' color={theme.palette.info.main} />
                          <Typography variant='caption' sx={{
                            fontSize: '0.75rem',
                            color: 'text.secondary'
                          }}>
                            {_t('current.sunset')}: {day.dayData.astro.sunset.split(' ')[0]}
                          </Typography>
                        </Stack>

                        {/* Umidade */}
                        <Stack direction='row' alignItems='center' spacing={0.4}>
                          <IconifyIcon icon='ri:drop-line' fontSize='0.8rem' color={theme.palette.info.main} />
                          <Typography variant='caption' sx={{
                            fontSize: '0.75rem',
                            color: 'text.secondary'
                          }}>
                            {_t('current.humidity')}: {day.hourlyData[0]?.humidity || 0}%
                          </Typography>
                        </Stack>
                      </Stack>

                      {/* Coluna 2: Wind, Rain, Visibility */}
                      <Stack spacing={0.3}>
                        {/* Vento */}
                        <Stack direction='row' alignItems='center' spacing={0.4}>
                          <IconifyIcon icon='ri:windy-line' fontSize='0.8rem' color={theme.palette.success.main} />
                          <Typography variant='caption' sx={{
                            fontSize: '0.75rem',
                            color: 'text.secondary'
                          }}>
                            {_t('current.wind')}: {formatWindSpeed(day.hourlyData[0]?.windSpeed || 0, data.unitSystem)}
                          </Typography>
                        </Stack>

                        {/* Precipitação */}
                        <Stack direction='row' alignItems='center' spacing={0.4}>
                          <IconifyIcon icon='ri:rainy-line' fontSize='0.8rem' color={theme.palette.primary.main} />
                          <Typography variant='caption' sx={{
                            fontSize: '0.75rem',
                            color: 'text.secondary'
                          }}>
                            {_t('forecast.precipitation')}: {day.hourlyData[0]?.chanceOfRain || 0}%
                          </Typography>
                        </Stack>

                        {/* Visibilidade */}
                        <Stack direction='row' alignItems='center' spacing={0.4}>
                          <IconifyIcon icon='ri:eye-line' fontSize='0.8rem' color={theme.palette.grey[600]} />
                          <Typography variant='caption' sx={{
                            fontSize: '0.75rem',
                            color: 'text.secondary'
                          }}>
                            {_t('current.visibility')}: {index === 0 ? formatVisibility(data.current.visibility, data.unitSystem) : '10km'}
                          </Typography>
                        </Stack>
                      </Stack>
                    </Stack>
                  </Stack>
                </Stack>
              )
            })}
          </Stack>
        </Box>

        {/* Layout Mobile: Carrossel com navegação */}
        <Box sx={{ display: { xs: 'block', md: 'none' } }}>
          <Stack direction='row' alignItems='center' spacing={1}>
            {/* Seta esquerda */}
            <IconButton
              onClick={() => navigateDay('prev')}
              disabled={currentDayIndex === 0}
              size='small'
              sx={{
                width: 32,
                height: 32,
                backgroundColor: theme.palette.action.hover,
                '&:hover': { backgroundColor: theme.palette.action.selected },
                '&:disabled': { opacity: 0.3 }
              }}
            >
              <IconifyIcon icon='ri:arrow-left-s-line' fontSize='1rem' />
            </IconButton>

            {/* Dia atual */}
            <Box sx={{ flex: 1 }}>
              {(() => {
                const day = data.forecast[currentDayIndex]

                const dayName = currentDayIndex === 0 ? _t('days.today') :
                               getDayOfWeek(day.date, currentLang === 'en' ? 'en' : 'pt')

                return (
                  <Stack spacing={1} sx={{ alignItems: 'center' }}>
                    {/* Nome do dia */}
                    <Typography variant='caption' fontWeight={600} sx={{
                      fontSize: '0.85rem',
                      color: 'text.primary',
                      textAlign: 'center'
                    }}>
                      {dayName}
                    </Typography>

                    {/* Ícone e temperaturas centralizados */}
                    <Stack alignItems='center' spacing={0.5}>
                      {/* Ícone do clima */}
                      <Box>
                        {renderWeatherIcon(day.dayData.condition.code, 36, true)}
                      </Box>

                      {/* Temperaturas */}
                      <Stack direction='row' alignItems='center' spacing={0.3}>
                        <IconifyIcon icon='ri:arrow-up-line' fontSize='0.75rem' color={theme.palette.error.main} />
                        <Typography variant='caption' fontWeight={600} sx={{
                          fontSize: '0.8rem',
                          color: 'text.primary'
                        }}>
                          {Math.round(day.dayData.temperature.max)}°
                        </Typography>
                        <IconifyIcon icon='ri:arrow-down-line' fontSize='0.75rem' color={theme.palette.info.main} />
                        <Typography variant='caption' sx={{
                          fontSize: '0.75rem',
                          color: 'text.secondary'
                        }}>
                          {Math.round(day.dayData.temperature.min)}°
                        </Typography>
                      </Stack>
                    </Stack>

                    {/* Informações em duas colunas compactas */}
                    <Stack direction='row' spacing={1.5} sx={{ justifyContent: 'center' }}>
                      {/* Coluna 1 */}
                      <Stack spacing={0.2}>
                        <Stack direction='row' alignItems='center' spacing={0.3}>
                          <IconifyIcon icon='ri:sun-line' fontSize='0.7rem' color={theme.palette.warning.main} />
                          <Typography variant='caption' sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>
                            {_t('current.sunrise')}: {day.dayData.astro.sunrise.split(' ')[0]}
                          </Typography>
                        </Stack>
                        <Stack direction='row' alignItems='center' spacing={0.3}>
                          <IconifyIcon icon='ri:moon-line' fontSize='0.7rem' color={theme.palette.info.main} />
                          <Typography variant='caption' sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>
                            {_t('current.sunset')}: {day.dayData.astro.sunset.split(' ')[0]}
                          </Typography>
                        </Stack>
                        <Stack direction='row' alignItems='center' spacing={0.3}>
                          <IconifyIcon icon='ri:drop-line' fontSize='0.7rem' color={theme.palette.info.main} />
                          <Typography variant='caption' sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>
                            {_t('current.humidity')}: {day.hourlyData[0]?.humidity || 0}%
                          </Typography>
                        </Stack>
                      </Stack>

                      {/* Coluna 2 */}
                      <Stack spacing={0.2}>
                        <Stack direction='row' alignItems='center' spacing={0.3}>
                          <IconifyIcon icon='ri:windy-line' fontSize='0.7rem' color={theme.palette.success.main} />
                          <Typography variant='caption' sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>
                            {_t('current.wind')}: {formatWindSpeed(day.hourlyData[0]?.windSpeed || 0, data.unitSystem)}
                          </Typography>
                        </Stack>
                        <Stack direction='row' alignItems='center' spacing={0.3}>
                          <IconifyIcon icon='ri:rainy-line' fontSize='0.7rem' color={theme.palette.primary.main} />
                          <Typography variant='caption' sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>
                            {_t('forecast.precipitation')}: {day.hourlyData[0]?.chanceOfRain || 0}%
                          </Typography>
                        </Stack>
                        <Stack direction='row' alignItems='center' spacing={0.3}>
                          <IconifyIcon icon='ri:eye-line' fontSize='0.7rem' color={theme.palette.grey[600]} />
                          <Typography variant='caption' sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>
                            {_t('current.visibility')}: {currentDayIndex === 0 ? formatVisibility(data.current.visibility, data.unitSystem) : '10km'}
                          </Typography>
                        </Stack>
                      </Stack>
                    </Stack>
                  </Stack>
                )
              })()}
            </Box>

            {/* Seta direita */}
            <IconButton
              onClick={() => navigateDay('next')}
              disabled={currentDayIndex >= data.forecast.length - 1}
              size='small'
              sx={{
                width: 32,
                height: 32,
                backgroundColor: theme.palette.action.hover,
                '&:hover': { backgroundColor: theme.palette.action.selected },
                '&:disabled': { opacity: 0.3 }
              }}
            >
              <IconifyIcon icon='ri:arrow-right-s-line' fontSize='1rem' />
            </IconButton>
          </Stack>
        </Box>
      </Box>

      {/* Gráfico de Temperatura Moderno */}
      <Box sx={{ mb: { xs: 2, md: 3 } }}>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: { xs: 1.5, md: 2 }
        }}>
          <Typography variant='body1' fontWeight={600} sx={{ fontSize: { xs: '0.8rem', md: '0.9rem' } }}>
            {_t('forecast.hourly')}
          </Typography>
          <Stack direction='row' spacing={{ xs: 0.5, md: 1 }}>
            <IconButton
              onClick={() => scrollChart('left')}
              size='small'
              sx={{
                width: { xs: 28, md: 32 },
                height: { xs: 28, md: 32 },
                backgroundColor: theme.palette.action.hover,
                '&:hover': { backgroundColor: theme.palette.action.selected }
              }}
            >
              <IconifyIcon icon='ri:arrow-left-s-line' fontSize='0.9rem' />
            </IconButton>
            <IconButton
              onClick={() => scrollChart('right')}
              size='small'
              sx={{
                width: { xs: 28, md: 32 },
                height: { xs: 28, md: 32 },
                backgroundColor: theme.palette.action.hover,
                '&:hover': { backgroundColor: theme.palette.action.selected }
              }}
            >
              <IconifyIcon icon='ri:arrow-right-s-line' fontSize='0.9rem' />
            </IconButton>
          </Stack>
        </Box>

        {/* Container do gráfico elegante */}
        <Box sx={{
          position: 'relative',
          height: { xs: 170, md: 175 },
          width: '100%',
          ...glassmorphismStyle,
          p: { xs: 1.5, md: 2 },
          borderRadius: 2,
          overflow: 'hidden'
        }}>
          {/* Área do gráfico com scroll horizontal */}
          <Box
            ref={chartRef}
            sx={{
              display: 'flex',
              overflowX: 'auto',
              overflowY: 'hidden',
              height: '100%',
              gap: 0.5,
              scrollBehavior: 'smooth',
              '&::-webkit-scrollbar': {
                height: 3,
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: 'transparent'
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: theme.palette.divider,
                borderRadius: 2
              }
            }}
          >
            {hourlyDataToShow.map((hour, index) => {
              const isActive = index === activeHourIndex
              const temp = hour.temperature
              const maxTemp = Math.max(...hourlyDataToShow.map(h => h.temperature))
              const minTemp = Math.min(...hourlyDataToShow.map(h => h.temperature))
              const tempRange = maxTemp - minTemp
              const normalizedHeight = tempRange > 0 ? ((temp - minTemp) / tempRange) * 50 + 20 : 35

              // Obter cor baseada na temperatura (mesmo esquema da versão compacta)
              const getTemperatureColor = (temperature: number) => {
                if (temperature >= 35) return '#FF5722' // Muito quente - vermelho
                if (temperature >= 30) return '#FF9800' // Quente - laranja
                if (temperature >= 25) return '#FFC107' // Morno - amarelo
                if (temperature >= 20) return '#4CAF50' // Agradável - verde
                if (temperature >= 15) return '#2196F3' // Fresco - azul
                if (temperature >= 10) return '#3F51B5' // Frio - azul escuro
                if (temperature >= 5) return '#9C27B0' // Muito frio - roxo

                return '#607D8B' // Congelante - cinza azulado
              }

              const hourTime = new Date(hour.time)
              const today = new Date()

              const isNextDay = hourTime.getDate() !== today.getDate() ||
                hourTime.getMonth() !== today.getMonth() ||
                hourTime.getFullYear() !== today.getFullYear()

              return (
                <Box
                  key={`${hour.time}-${index}`}
                  onClick={() => setSelectedHourIndex(index)}
                  onMouseEnter={() => setHoveredHourIndex(index)}
                  onMouseLeave={() => setHoveredHourIndex(null)}
                  sx={{
                    minWidth: { xs: 60, md: 70 },
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    height: '100%',
                    cursor: 'pointer',
                    position: 'relative',
                    py: 1,
                    transition: 'all 0.2s ease',
                    transform: isActive ? 'scale(1.02)' : 'scale(1)',
                    opacity: isActive ? 1 : 0.8,
                    '&:hover': {
                      opacity: 1,
                      transform: 'scale(1.02)'
                    }
                  }}
                >
                  {/* Temperatura */}
                  <Typography
                    variant='caption'
                    fontWeight={600}
                    sx={{
                      fontSize: { xs: '0.8rem', md: '0.9rem' },
                      color: isActive ? theme.palette.primary.main : 'text.primary',
                      mb: 0.5
                    }}
                  >
                    {temp}°
                  </Typography>

                  {/* Linha de temperatura */}
                  <Box sx={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'end',
                    justifyContent: 'center',
                    width: '100%',
                    position: 'relative'
                  }}>
                                      <Box sx={{
                    width: 2,
                    height: normalizedHeight,
                    background: isActive
                      ? `linear-gradient(to top, ${getTemperatureColor(temp)}, ${getTemperatureColor(temp)}AA)`
                      : `linear-gradient(to top, ${getTemperatureColor(temp)}66, ${getTemperatureColor(temp)}99)`,
                    borderRadius: 1,
                    transition: 'all 0.2s ease'
                  }} />

                  {/* Ponto no topo da linha */}
                  <Box sx={{
                    position: 'absolute',
                    top: `${100 - (normalizedHeight / 70 * 100)}%`,
                    width: 5,
                    height: 5,
                    borderRadius: '50%',
                    backgroundColor: getTemperatureColor(temp),
                    border: `1px solid ${theme.palette.background.paper}`,
                    transition: 'all 0.2s ease',
                    opacity: isActive ? 1 : 0.8
                  }} />
                  </Box>

                  {/* Ícone do clima */}
                  <Box sx={{
                    my: 0.5,
                    '& > div': { width: { xs: 24, md: 28 }, height: { xs: 24, md: 28 } }
                  }}>
                    {renderWeatherIcon(
                      hour.condition.code,
                      50,
                      getDayPeriodInfo(hour.time).isDay,
                      hour.time
                    )}
                  </Box>

                  {/* Horário */}
                  <Typography variant='caption' sx={{
                    fontSize: { xs: '0.7rem', md: '0.75rem' },
                    fontWeight: 500,
                    color: 'text.secondary'
                  }}>
                    {formatTimeString(hour.time)}
                  </Typography>

                  {/* Indicador de próximo dia */}
                  {isNextDay && (
                    <Box sx={{
                      position: 'absolute',
                      top: -2,
                      right: -2,
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      backgroundColor: theme.palette.secondary.main
                    }} />
                  )}

                  {/* Chance de chuva */}
                  {hour.chanceOfRain > 0 && (
                    <Stack direction='row' alignItems='center' spacing={0.2} sx={{ mt: 0.2 }}>
                      <IconifyIcon icon='ri:drop-line' fontSize='0.6rem' color={theme.palette.info.main} />
                      <Typography variant='caption' sx={{ fontSize: '0.6rem', color: theme.palette.info.main }}>
                        {hour.chanceOfRain}%
                      </Typography>
                    </Stack>
                  )}
                </Box>
              )
            })}
          </Box>
        </Box>
      </Box>

      {/* Detalhes da Hora Selecionada - Compacto e Elegante */}
      {activeHourData && (
        <Box sx={{ mb: { xs: 2, md: 3 } }}>
                    <Typography variant='body2' fontWeight={600} sx={{
            mb: { xs: 1.5, md: 2 },
            textAlign: 'center',
            fontSize: { xs: '0.9rem', md: '1rem' }
          }}>
            {`${getActiveDayName()}, ${formatTimeString(activeHourData.time)} - ${activeHourData.condition.text}`}
          </Typography>

          <Box sx={{
            ...glassmorphismStyle,
            p: { xs: 1, md: 1.5 },
            borderRadius: 2
          }}>
            <Stack
              direction='row'
              spacing={{ xs: 1, md: 2 }}
              sx={{
                justifyContent: 'space-around',
                flexWrap: 'wrap',
                gap: { xs: 0.5, md: 1 }
              }}
            >
              {/* Sensação Térmica */}
              <Stack alignItems='center' spacing={0.2} sx={{ minWidth: { xs: 50, md: 60 } }}>
                <IconifyIcon icon='ri:thermometer-line' fontSize='0.75rem' color={theme.palette.warning.main} />
                <Typography variant='caption' color='text.secondary' sx={{ fontSize: { xs: '0.65rem', md: '0.7rem' } }}>
                  {_t('current.feelsLike')}
                </Typography>
                <Typography variant='body2' fontWeight={600} sx={{ fontSize: { xs: '0.75rem', md: '0.8rem' } }}>
                  {formatTemperature(activeHourData.feelsLike || 0, data.unitSystem)}
                </Typography>
              </Stack>

              {/* Umidade */}
              <Stack alignItems='center' spacing={0.2} sx={{ minWidth: { xs: 50, md: 60 } }}>
                <IconifyIcon icon='ri:drop-line' fontSize='0.75rem' color={theme.palette.info.main} />
                <Typography variant='caption' color='text.secondary' sx={{ fontSize: { xs: '0.65rem', md: '0.7rem' } }}>
                  {_t('current.humidity')}
                </Typography>
                <Typography variant='body2' fontWeight={600} sx={{ fontSize: { xs: '0.75rem', md: '0.8rem' } }}>
                  {formatPercentage(activeHourData.humidity || 0)}
                </Typography>
              </Stack>

              {/* Vento */}
              <Stack alignItems='center' spacing={0.2} sx={{ minWidth: { xs: 50, md: 60 } }}>
                <IconifyIcon icon='ri:windy-line' fontSize='0.75rem' color={theme.palette.success.main} />
                <Typography variant='caption' color='text.secondary' sx={{ fontSize: { xs: '0.65rem', md: '0.7rem' } }}>
                  {_t('current.wind')}
                </Typography>
                <Typography variant='body2' fontWeight={600} sx={{ fontSize: { xs: '0.75rem', md: '0.8rem' } }}>
                  {formatWindSpeed(activeHourData.windSpeed || 0, data.unitSystem)}
                </Typography>
              </Stack>

              {/* Pressão */}
              <Stack alignItems='center' spacing={0.2} sx={{ minWidth: { xs: 50, md: 60 } }}>
                <IconifyIcon icon='ri:dashboard-line' fontSize='0.75rem' color={theme.palette.grey[600]} />
                <Typography variant='caption' color='text.secondary' sx={{ fontSize: { xs: '0.65rem', md: '0.7rem' } }}>
                  {_t('current.pressure')}
                </Typography>
                <Typography variant='body2' fontWeight={600} sx={{ fontSize: { xs: '0.75rem', md: '0.8rem' } }}>
                  {formatPressure(activeHourData.pressure || 0, data.unitSystem)}
                </Typography>
              </Stack>

              {/* Visibilidade */}
              <Stack alignItems='center' spacing={0.2} sx={{ minWidth: { xs: 50, md: 60 } }}>
                <IconifyIcon icon='ri:eye-line' fontSize='0.75rem' color={theme.palette.grey[600]} />
                <Typography variant='caption' color='text.secondary' sx={{ fontSize: { xs: '0.65rem', md: '0.7rem' } }}>
                  {_t('current.visibility')}
                </Typography>
                <Typography variant='body2' fontWeight={600} sx={{ fontSize: { xs: '0.75rem', md: '0.8rem' } }}>
                  {formatVisibility(data.current.visibility, data.unitSystem)}
                </Typography>
              </Stack>

              {/* UV Index */}
              <Stack alignItems='center' spacing={0.2} sx={{ minWidth: { xs: 50, md: 60 } }}>
                <IconifyIcon
                  icon='ri:sun-line'
                  fontSize='0.75rem'
                  color={getUVIndexColor(data.current.uv)}
                />
                <Typography variant='caption' color='text.secondary' sx={{ fontSize: { xs: '0.65rem', md: '0.7rem' } }}>
                  {_t('current.uvIndex')}
                </Typography>
                <Typography variant='body2' fontWeight={600} sx={{ fontSize: { xs: '0.75rem', md: '0.8rem' } }}>
                  {formatUVIndex(data.current.uv)}
                </Typography>
              </Stack>

              {/* Ponto de Orvalho */}
              <Stack alignItems='center' spacing={0.2} sx={{ minWidth: { xs: 50, md: 60 } }}>
                <IconifyIcon icon='ri:temp-cold-line' fontSize='0.75rem' color={theme.palette.info.main} />
                <Typography variant='caption' color='text.secondary' sx={{ fontSize: { xs: '0.65rem', md: '0.7rem' } }}>
                  {_t('current.dewPoint')}
                </Typography>
                <Typography variant='body2' fontWeight={600} sx={{ fontSize: { xs: '0.75rem', md: '0.8rem' } }}>
                  {formatTemperature(data.current.dewPoint, data.unitSystem)}
                </Typography>
              </Stack>

              {/* Qualidade do Ar */}
              <Stack alignItems='center' spacing={0.2} sx={{ minWidth: { xs: 50, md: 60 } }}>
                <IconifyIcon
                  icon='ri:leaf-line'
                  fontSize='0.75rem'
                  color={getAirQualityColorByIndex(data.current.airQuality.us_epa_index)}
                />
                <Typography variant='caption' color='text.secondary' sx={{ fontSize: { xs: '0.65rem', md: '0.7rem' } }}>
                  {_t('current.airQuality')}
                </Typography>
                <Typography variant='body2' fontWeight={600} sx={{ fontSize: { xs: '0.75rem', md: '0.8rem' } }}>
                  {formatAirQuality(data.current.airQuality)}
                </Typography>
              </Stack>
              </Stack>
              {/* Segunda linha - Informações adicionais */}
              <Stack
                direction='row'
                spacing={{ xs: 1, md: 2 }}
                sx={{
                  justifyContent: 'space-between',
                  gap: { xs: 0.5, md: 1 },
                  mt: { xs: 1.5, md: 2 },
                  mx: { xs: 3, md: 3 }
                }}
              >
                {/* Probabilidade de Chuva */}
                <Stack alignItems='center' spacing={0.2} sx={{ minWidth: { xs: 50, md: 60 } }}>
                  <IconifyIcon icon='ri:rainy-line' fontSize='0.75rem' color={theme.palette.info.main} />
                  <Typography variant='caption' color='text.secondary' sx={{ fontSize: { xs: '0.65rem', md: '0.7rem' } }}>
                    {_t('current.chanceOfRain')}
                  </Typography>
                  <Typography variant='body2' fontWeight={600} sx={{ fontSize: { xs: '0.75rem', md: '0.8rem' } }}>
                    {activeHourData.chanceOfRain || 0}%
                  </Typography>
                </Stack>

                {/* Rajadas de Vento */}
                <Stack alignItems='center' spacing={0.2} sx={{ minWidth: { xs: 50, md: 60 } }}>
                  <IconifyIcon icon='ri:windy-line' fontSize='0.75rem' color={theme.palette.warning.main} />
                  <Typography variant='caption' color='text.secondary' sx={{ fontSize: { xs: '0.65rem', md: '0.7rem' } }}>
                    {_t('current.windGust')}
                  </Typography>
                  <Typography variant='body2' fontWeight={600} sx={{ fontSize: { xs: '0.75rem', md: '0.8rem' } }}>
                    {formatWindSpeed((activeHourData as any).windGust || 0, data.unitSystem)}
                  </Typography>
                </Stack>

                {/* Direção do Vento */}
                <Stack alignItems='center' spacing={0.2} sx={{ minWidth: { xs: 50, md: 60 } }}>
                  <IconifyIcon icon='ri:navigation-line' fontSize='0.75rem' color={theme.palette.success.main} />
                  <Typography variant='caption' color='text.secondary' sx={{ fontSize: { xs: '0.65rem', md: '0.7rem' } }}>
                    {_t('current.windDirectionShort')}
                  </Typography>
                  <Typography variant='body2' fontWeight={600} sx={{ fontSize: { xs: '0.75rem', md: '0.8rem' } }}>
                    {activeHourData.windDirection || 'N/A'}
                  </Typography>
                </Stack>

                {/* Nebulosidade */}
                <Stack alignItems='center' spacing={0.2} sx={{ minWidth: { xs: 50, md: 60 } }}>
                  <IconifyIcon icon='ri:cloudy-line' fontSize='0.75rem' color={theme.palette.grey[600]} />
                  <Typography variant='caption' color='text.secondary' sx={{ fontSize: { xs: '0.65rem', md: '0.7rem' } }}>
                    {_t('current.cloudiness')}
                  </Typography>
                  <Typography variant='body2' fontWeight={600} sx={{ fontSize: { xs: '0.75rem', md: '0.8rem' } }}>
                    {(activeHourData as any).cloud || 0}%
                  </Typography>
                </Stack>

                {/* Precipitação */}
                <Stack alignItems='center' spacing={0.2} sx={{ minWidth: { xs: 50, md: 60 } }}>
                  <IconifyIcon icon='ri:drop-line' fontSize='0.75rem' color={theme.palette.primary.main} />
                  <Typography variant='caption' color='text.secondary' sx={{ fontSize: { xs: '0.65rem', md: '0.7rem' } }}>
                    {_t('current.precipitation')}
                  </Typography>
                  <Typography variant='body2' fontWeight={600} sx={{ fontSize: { xs: '0.75rem', md: '0.8rem' } }}>
                    {(activeHourData as any).precipitation || 0}mm
                  </Typography>
                </Stack>

                {/* Índice de Calor */}
                <Stack alignItems='center' spacing={0.2} sx={{ minWidth: { xs: 50, md: 60 } }}>
                  <IconifyIcon icon='ri:fire-line' fontSize='0.75rem' color={theme.palette.error.main} />
                  <Typography variant='caption' color='text.secondary' sx={{ fontSize: { xs: '0.65rem', md: '0.7rem' } }}>
                    {_t('current.heatIndex')}
                  </Typography>
                  <Typography variant='body2' fontWeight={600} sx={{ fontSize: { xs: '0.75rem', md: '0.8rem' } }}>
                    {formatTemperature((activeHourData as any).heatIndex || 0, data.unitSystem)}
                  </Typography>
                </Stack>
              </Stack>

          </Box>
        </Box>
      )}
    </Box>
  )

  if (!isExpanded || !originalPosition) return null

  return (
    <>
      {/* Backdrop */}
      <Backdrop
        open={isExpanded}
        onClick={onClose}
        sx={{
          zIndex: 1200,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          backdropFilter: 'blur(4px)'
        }}
      />

            {/* Overlay Card */}
      <Box
        sx={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '95vw', sm: Math.max(originalPosition.width * 1.05) },
          maxWidth: { xs: '95vw', sm: 'none' },
          height: { xs: '85vh', sm: '80vh' },
          maxHeight: '95vh',
          zIndex: 1300,
          transformOrigin: 'center'
        }}
      >
        <Card
          elevation={0}
          sx={{
            ...glassmorphismStyle,
            transition: 'all 0.3s ease',
            transform: isExpanded ? 'scale(1) translateY(-10px)' : 'scale(1)',
            boxShadow:
              theme.palette.mode === 'dark'
                ? '0 20px 60px rgba(0, 0, 0, 0.6), inset 0 1px 0 rgba(255, 255, 255, 0.1)'
                : '0 20px 60px rgba(31, 38, 135, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.8)',
            borderRadius: 3
          }}
        >
          <CardContent
            sx={{
              p: { xs: 2, md: 2.5 },
              position: 'relative',
              height: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
              '&::-webkit-scrollbar': {
                width: 4,
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: 'transparent'
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: theme.palette.divider,
                borderRadius: 2
              }
            }}
          >
            {/* Botão de fechamento */}
            <IconButton
              onClick={(e) => {
                e.stopPropagation()
                onClose()
              }}
              sx={{
                position: 'absolute',
                right: { xs: 8, md: 12 },
                top: { xs: 8, md: 12 },
                zIndex: 10,
                backgroundColor: theme.palette.mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.1)'
                  : 'rgba(0, 0, 0, 0.1)',
                '&:hover': {
                  backgroundColor: theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.2)'
                    : 'rgba(0, 0, 0, 0.2)'
                }
              }}
            >
              <IconifyIcon icon='ri:close-line' fontSize='1.1rem' />
            </IconButton>

            {/* Vista compacta sempre visível */}
            <CompactView
              data={data}
              isExpanded={isExpanded}
              renderWeatherIcon={renderWeatherIcon}
            />

            {/* Vista expandida */}
            {renderExpandedContent()}
          </CardContent>
        </Card>
      </Box>
    </>
  )
}

export default ExpandedOverlay
